<template>
  <div class="tags-filter" ref="dropdownRef">
    <!-- WCAG 4.1.2: button provides keyboard access + aria-expanded -->
    <button
      type="button"
      class="filter-trigger"
      :class="{ active: isOpen || hasSelections }"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      :aria-controls="panelId"
      :id="triggerId"
      @mouseenter="showOptions"
      @mouseleave="hideOptions"
      @click="toggleOptions"
    >
      <div class="trigger-content">
        <span class="filter-label">{{ label }}</span>
      </div>
      <!-- Decorative chevron SVG hidden from AT (WCAG 1.1.1) -->
      <div class="filter-arrow" aria-hidden="true">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M9 6L15 12L9 18"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </button>

    <Transition name="dropdown">
      <Teleport to="body">
        <div
          v-if="isOpen"
          :id="panelId"
          ref="optionsPanelRef"
          class="options-panel"
          :style="panelStyle"
          role="dialog"
          aria-modal="false"
          :aria-labelledby="triggerId"
          @mouseenter="keepOpen"
          @mouseleave="hideOptions"
        >
          <div class="options-container">
            <div
              v-for="category in categorizedTags"
              :key="category.category"
              class="category-section"
            >
              <!-- WCAG 4.1.2: category toggle is a button -->
              <button
                type="button"
                class="category-header"
                :aria-expanded="!!expandedCategories[category.category]"
                @click="toggleCategory(category.category)"
              >
                <span class="category-title">{{ category.category }}</span>
                <!-- Decorative chevron -->
                <div
                  class="category-arrow"
                  :class="{ expanded: expandedCategories[category.category] }"
                  aria-hidden="true"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </button>

              <div
                v-if="expandedCategories[category.category]"
                class="tags-section"
              >
                <div class="tags-container">
                  <!-- WCAG 4.1.2: tag chips are checkbox widgets with keyboard support -->
                  <div
                    v-for="tag in getVisibleTags(category)"
                    :key="tag.id"
                    class="tag-item"
                    role="checkbox"
                    :aria-checked="isSelected(tag.tag)"
                    :tabindex="0"
                    :class="{ selected: isSelected(tag.tag) }"
                    @click="toggleSelection(tag)"
                    @keydown.space.prevent="toggleSelection(tag)"
                    @keydown.enter.prevent="toggleSelection(tag)"
                  >
                    <span class="tag-text">{{ tag.tag }}</span>
                  </div>
                </div>

                <!-- WCAG 4.1.2: view-more is a button -->
                <button
                  v-if="
                    category.tag.length > 5 &&
                    !showMoreCategories[category.category]
                  "
                  type="button"
                  class="view-more-button"
                  :aria-label="'View more ' + category.category + ' tags'"
                  @click="toggleShowMore(category.category)"
                >
                  <span class="view-more-text">View More</span>
                  <div class="view-more-arrow" aria-hidden="true">
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Teleport>
    </Transition>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  defineProps,
  defineEmits,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  options: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(["update:modelValue", "change", "clear"]);

const dropdownRef = ref(null);
const optionsPanelRef = ref(null);
const panelStyle = ref({});
const isOpen = ref(false);
const expandedCategories = ref({});
const showMoreCategories = ref({});
let hoverTimeout = null;

// Unique IDs for aria-controls/aria-labelledby pairing (WCAG 4.1.2)
const triggerId = `tags-trigger-${Math.random().toString(36).slice(2, 8)}`;
const panelId = `tags-panel-${Math.random().toString(36).slice(2, 8)}`;

const selectedItems = computed(() => props.modelValue || []);
const hasSelections = computed(() => selectedItems.value.length > 0);

// Process categorized tags and sort by count
const categorizedTags = computed(() => {
  return props.options.map((category) => ({
    ...category,
    tag: [...category.tag].sort((a, b) => b.count - a.count),
  }));
});

// Initialize expanded state for all categories
watch(
  () => props.options,
  (newOptions) => {
    if (newOptions && newOptions.length > 0) {
      const expanded = {};
      const showMore = {};
      newOptions.forEach((category) => {
        expanded[category.category] = true;
        showMore[category.category] = false;
      });
      expandedCategories.value = expanded;
      showMoreCategories.value = showMore;
    }
  },
  { immediate: true }
);

const getVisibleTags = (category) => {
  const isShowingMore = showMoreCategories.value[category.category];
  return isShowingMore ? category.tag : category.tag.slice(0, 5);
};

const isSelected = (tagName) => {
  const selectedValues = Array.isArray(selectedItems.value)
    ? selectedItems.value.map((v) =>
        typeof v === "object" ? (v.tag ?? v.title ?? v.value ?? v) : v
      )
    : [];
  return selectedValues.includes(tagName);
};

const showOptions = () => {
  clearTimeout(hoverTimeout);
  isOpen.value = true;
  nextTick(() => {
    computePanelPosition();
  });
};

const hideOptions = () => {
  hoverTimeout = setTimeout(() => {
    isOpen.value = false;
  }, 150);
};

const keepOpen = () => {
  clearTimeout(hoverTimeout);
};

const toggleOptions = () => {
  if (isOpen.value) {
    hideOptions();
  } else {
    showOptions();
  }
};

const toggleCategory = (categoryName) => {
  expandedCategories.value[categoryName] =
    !expandedCategories.value[categoryName];
};

const toggleShowMore = (categoryName) => {
  showMoreCategories.value[categoryName] =
    !showMoreCategories.value[categoryName];
};

const toggleSelection = (tag) => {
  const current = Array.isArray(selectedItems.value)
    ? selectedItems.value.map((v) =>
        typeof v === "object" ? (v.tag ?? v.title ?? v.value ?? v) : v
      )
    : [];
  const tagName = tag.tag;
  const index = current.indexOf(tagName);
  const next = [...current];
  if (index === -1) {
    next.push(tagName);
  } else {
    next.splice(index, 1);
  }

  emit("update:modelValue", next);
  emit("change", {
    value: next,
    tag: {
      id: tag.id,
      title: tag.tag,
      value: tagName,
      count: tag.count,
      category: tag.category,
    },
    action: index === -1 ? "add" : "remove",
  });
};

const computePanelPosition = () => {
  const margin = 12;
  const trigger = dropdownRef.value;
  const panel = optionsPanelRef.value;
  if (!trigger || !panel) return;

  const triggerRect = trigger.getBoundingClientRect();
  const panelWidth = panel.offsetWidth || 360;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let left = triggerRect.right + 20;
  if (left + panelWidth + margin > viewportWidth) {
    left = triggerRect.left - panelWidth - 20;
    if (left < margin)
      left = Math.max(margin, viewportWidth - panelWidth - margin);
  }

  let top = triggerRect.top;
  const maxHeight = Math.max(240, viewportHeight - margin * 2);
  const clampedTop = Math.min(
    Math.max(margin, top),
    viewportHeight - margin - 40
  );

  panelStyle.value = {
    position: "fixed",
    top: `${clampedTop}px`,
    left: `${left}px`,
    maxHeight: `${maxHeight}px`,
    transform: "none",
  };
};

const onWindowChange = () => {
  if (isOpen.value) computePanelPosition();
};

onMounted(() => {
  window.addEventListener("resize", onWindowChange);
  window.addEventListener("scroll", onWindowChange, true);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onWindowChange);
  window.removeEventListener("scroll", onWindowChange, true);
});
</script>

<style scoped>
.tags-filter {
  position: relative;
  width: 100%;
}

.filter-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: 100%;
  border: none;
  background: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
}

/* Visible focus ring for keyboard users (WCAG 2.4.7) */
.filter-trigger:focus-visible {
  outline: 3px solid var(--primary-green, #00925e);
  outline-offset: 2px;
}

.filter-trigger:hover {
  background-color: var(--primary-green);
}

.filter-trigger.active {
  background-color: var(--primary-green);
}

.filter-trigger:hover .filter-label,
.filter-trigger:hover .filter-arrow,
.filter-trigger.active .filter-label,
.filter-trigger.active .filter-arrow {
  color: #ffffff;
}

.trigger-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.filter-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-big);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.filter-arrow {
  color: #007a50;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.filter-arrow svg {
  width: 18px;
  height: 18px;
}

.options-panel {
  position: fixed;
  top: 0;
  left: 0;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1001;
  min-width: 350px;
  max-width: 400px;
}

.options-container {
  max-height: 600px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}

.options-container::-webkit-scrollbar {
  width: 6px;
}

.options-container::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.options-container::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.options-container::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.category-section {
  border-bottom: 1px solid #e5e7eb;
}

.category-section:last-child {
  border-bottom: none;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  background-color: #ffffff;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: 100%;
  border: none;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  text-align: left;
}

.category-header:focus-visible {
  outline: 3px solid var(--primary-green, #00925e);
  outline-offset: 2px;
}

.category-header:hover {
  background-color: #f9fafb;
}

.category-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.category-arrow {
  color: #007a50;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: rotate(180deg);
}

.category-arrow.expanded {
  transform: rotate(0deg);
}

.category-arrow svg {
  width: 18px;
  height: 18px;
}

.tags-section {
  padding: 0 8px 8px 8px;
}

.tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 8px;
  background-color: #ffffff;
  border: none;
}

.tag-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #ffffff;
  border: 1px solid #dfe2e7;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: var(--fs-small);
}

.tag-item:hover {
  background-color: #f9fafb;
  border-color: #00925e;
}

.tag-item.selected {
  background-color: #f0f9ff;
  border-color: #00925e;
  color: #007a50;
}

.tag-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  line-height: 20px;
  color: #40444b;
  letter-spacing: 0.035px;
  user-select: none;
}

.tag-item.selected .tag-text {
  color: #007a50;
}

.checkbox-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.checkbox-wrapper input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-visual {
  width: 16px;
  height: 16px;
  background-color: #ffffff;
  border: 2px solid #00925e;
  border-radius: 3px;
  position: relative;
  transition: all 0.2s ease;
}

.checkbox-wrapper input[type="checkbox"]:checked + .checkbox-visual {
  background-color: #00925e;
  border-color: #00925e;
}

.checkbox-visual:after {
  content: "";
  position: absolute;
  display: none;
}

.checkbox-wrapper input[type="checkbox"]:checked + .checkbox-visual:after {
  display: block;
}

.checkbox-wrapper .checkbox-visual:after {
  left: 4px;
  top: 1px;
  width: 3px;
  height: 6px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.view-more-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-radius: 12px;
  margin: 8px;
  /* Reset native <button> styles (WCAG 4.1.2) */
  width: calc(100% - 16px);
  border: none;
  background: none;
  font-family: inherit;
}

.view-more-button:hover {
  background-color: #f9fafb;
}

.view-more-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #007a50;
  letter-spacing: 0.024px;
}

.view-more-arrow {
  color: #007a50;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.view-more-arrow svg {
  width: 18px;
  height: 18px;
}

/* Dropdown animation */
.dropdown-enter-active {
  transition: all 0.2s ease;
}

.dropdown-leave-active {
  transition: all 0.15s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateX(-8px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .options-panel {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    max-width: 90vw;
    max-height: 80vh;
    min-width: 320px;
  }

  .filter-trigger {
    padding: 8px 12px;
    height: 40px;
  }

  .filter-label {
    font-size: var(--fs-small);
  }

  .category-title {
    font-size: var(--fs-small);
  }

  .view-more-text {
    font-size: var(--fs-small);
  }
}
</style>
